import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Trophy, Users, Check, X } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Select from '../../components/Select';
import Badge from '../../components/Badge';
import { getBasketballCompetitions, getTeamsInCompetition } from '../../utils/api';

export default function TeamsManagement() {
    const navigate = useNavigate();
    const [selectedCompetition, setSelectedCompetition] = useState(null);

    // Fetch competitions
    const { data: competitionsData, isLoading: competitionsLoading } = useQuery({
        queryKey: ['basketball-competitions'],
        queryFn: getBasketballCompetitions
    });

    // Set first competition as default
    useEffect(() => {
        if (competitionsData?.data?.length > 0 && !selectedCompetition) {
            setSelectedCompetition(competitionsData.data[0]._id);
        }
    }, [competitionsData, selectedCompetition]);

    // Fetch teams
    const { data: teamsData, isLoading: teamsLoading } = useQuery({
        queryKey: ['basketball-teams', selectedCompetition],
        queryFn: () => getTeamsInCompetition(selectedCompetition),
        enabled: !!selectedCompetition
    });

    const columns = [
        {
            header: 'Team',
            accessor: 'name',
            render: (team) => (
                <div className="flex items-center gap-3">
                    {team.logoUrl ? (
                        <img
                            src={team.logoUrl}
                            alt={team.name}
                            className="w-10 h-10 rounded-lg object-contain bg-gray-100 dark:bg-gray-800"
                        />
                    ) : (
                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                            <Users size={20} className="text-gray-400" />
                        </div>
                    )}
                    <span className="font-medium">{team.name}</span>
                </div>
            )
        },
        {
            header: 'Custom Roster',
            accessor: 'hasCustomRoster',
            render: (team) => (
                <div className="flex items-center gap-2">
                    {team.hasCustomRoster ? (
                        <>
                            <Badge variant="success">
                                <Check size={12} className="mr-1" />
                                Custom
                            </Badge>
                            <span className="text-sm text-muted-light dark:text-muted-dark">
                                ({team.customRosterCount} members)
                            </span>
                        </>
                    ) : (
                        <Badge variant="default">
                            <X size={12} className="mr-1" />
                            Genius Only
                        </Badge>
                    )}
                </div>
            )
        },
        {
            header: 'Actions',
            accessor: 'actions',
            render: (team) => (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/basketball/teams/${team._id}/roster`);
                    }}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                    Manage Roster
                </button>
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold mb-2">Teams Management</h1>
                <p className="text-muted-light dark:text-muted-dark">
                    Manage custom rosters for teams
                </p>
            </div>

            {/* Competition Selector */}
            <Card>
                <div className="flex items-center gap-4">
                    <Trophy size={20} className="text-primary" />
                    <Select
                        value={selectedCompetition || ''}
                        onChange={(e) => setSelectedCompetition(e.target.value)}
                        className="flex-1"
                        disabled={competitionsLoading}
                    >
                        <option value="">Select Competition</option>
                        {competitionsData?.data?.map(comp => (
                            <option key={comp._id} value={comp._id}>
                                {comp.name} ({comp.season})
                            </option>
                        ))}
                    </Select>
                </div>
            </Card>

            {/* Teams Table */}
            <Card>
                <Table
                    columns={columns}
                    data={teamsData?.data || []}
                    loading={teamsLoading || !selectedCompetition}
                    onRowClick={(team) => navigate(`/basketball/teams/${team._id}/roster`)}
                    emptyState={{
                        title: 'No teams found',
                        description: selectedCompetition
                            ? 'No teams in this competition'
                            : 'Select a competition to view teams'
                    }}
                />
            </Card>
        </div>
    );
}

